import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts";

import {
  defaultColorPalette,
  getCartesianChartData,
  cartesianChartStyling,
  makeAbbrevValueFormatter,
  buildFullByAbbrevMap,
  makeNumericValueFormatter,
} from "./utils";

interface BarGraphProps {
  children?: React.ReactNode;
  content: Array<{ [key: string]: any }>;
  xAxisKey: string;
  yAxisKey: string;
  /** Chart height in px. Defaults to the conversation size; dashboard tiles
   *  pass their own so the chart fills the tile. */
  height?: number;
}

const BarGraph: React.FC<BarGraphProps> = ({
  content,
  xAxisKey,
  yAxisKey,
  height = 350,
}) => {
  const theme = useTheme();
  const colorPalette = [theme.palette.primary.main, ...defaultColorPalette];

  const { xLabel, yLabel, abbreviatedContent, series } = getCartesianChartData(
    content,
    xAxisKey,
    colorPalette,
    yAxisKey,
  );

  // build a quick lookup: abbr -> full
  const fullByAbbrev = React.useMemo(
    () => buildFullByAbbrevMap(abbreviatedContent, xAxisKey),
    [abbreviatedContent, xAxisKey],
  );

  // --- responsive width via ResizeObserver ---
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setContainerWidth(cr.width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const width = Math.max(320, containerWidth);

  const margin = {
    left: Math.max(20, width * 0.07),
    right: Math.max(10, width * 0.03),
    top: 30,
    bottom: Math.max(20, width * 0.1),
  };

  return (
    <div ref={containerRef} style={{ width: "100%", height }}>
      {width > 0 && (
        <BarChart
          dataset={abbreviatedContent}
          xAxis={[
            {
              scaleType: "band",
              dataKey: xAxisKey,
              label: xLabel,
              valueFormatter: makeAbbrevValueFormatter(fullByAbbrev),
            },
          ]}
          series={series}
          yAxis={[
            { label: yLabel, valueFormatter: makeNumericValueFormatter() },
          ]}
          height={height}
          width={width}
          margin={margin}
          slotProps={{ tooltip: { trigger: "axis" } }} // make tooltip show the x value
          {...cartesianChartStyling}
        />
      )}
    </div>
  );
};

export const validateBarGraph = (data: any[], xAxis?: string): boolean => {
  if (!Array.isArray(data) || data.length === 0) return false;
  if (typeof data[0] !== "object") return false;
  const keys = Object.keys(data[0]);
  return xAxis !== undefined && keys.includes(xAxis);
};

export default BarGraph;
